import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { MarketFilters } from '../hooks/use-market-filters';
import type { FilterOptions } from '../lib/api-client';
import { KIND_LABELS } from '../lib/boat-kinds';
import { ftToMaxM, ftToMinM, maxMToFt, minMToFt } from '../lib/length-filter';
import { EquipmentFilter, SearchableMultiSelect } from './market-filter-panel';

interface WizardFilterPanelProps {
  filters: MarketFilters;
  setFilter: <K extends keyof MarketFilters>(key: K, value: MarketFilters[K]) => void;
  filterOptions?: FilterOptions;
  modelOptions?: { name: string; count: number }[];
}

export function WizardFilterPanel({
  filters,
  setFilter,
  filterOptions,
  modelOptions,
}: WizardFilterPanelProps) {
  const equipmentOptions = filterOptions?.equipment ?? [];
  const kindOptions = filterOptions?.kinds ?? [];

  // Count of active "advanced" filters. Length / Cabins / Berths each count
  // once whether From, To, or both are set. Drives the badge on the trigger
  // and the auto-open behaviour for deep-link URLs.
  const activeAdvanced = useMemo(() => {
    let n = 0;
    if (filters.minLength !== undefined || filters.maxLength !== undefined) n++;
    if (filters.minCabins !== undefined || filters.maxCabins !== undefined) n++;
    if (filters.minBerths !== undefined || filters.maxBerths !== undefined) n++;
    if (filters.minWc !== undefined) n++;
    if (filters.minPeople !== undefined) n++;
    if (filters.equipmentIds.length > 0) n++;
    return n;
  }, [filters]);

  const [advancedOpen, setAdvancedOpen] = useState(activeAdvanced > 0);
  // Auto-open when an advanced filter becomes active (e.g. deep-link sets
  // minCabins). Do NOT auto-close when all clear — respect manual collapse.
  useEffect(() => {
    if (activeAdvanced > 0) setAdvancedOpen(true);
  }, [activeAdvanced]);

  return (
    <div className="space-y-4">
      {/* Primary row: Type · Model · Year — the three filters users reach for
          first. Year is here (not under Advanced) because it shifts pricing
          significantly and is a primary "what kind of boat" axis. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Boat Type">
          <Select
            value={filters.kind || '_all'}
            onValueChange={(v) => {
              const next = v === '_all' ? '' : v;
              setFilter('kind', next);
              // Clear models when type changes — they're scoped to a kind.
              if (next !== filters.kind) setFilter('models', []);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All types</SelectItem>
              {kindOptions.map((k) => (
                <SelectItem key={k} value={k}>
                  {KIND_LABELS[k] ?? k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Model">
          <SearchableMultiSelect
            label=""
            selected={filters.models}
            onChange={(v) => setFilter('models', v)}
            options={(filters.kind ? modelOptions : undefined)?.map((m) => m.name) ?? []}
            placeholder={filters.kind ? 'Any model' : 'Select a type first'}
          />
        </Field>

        <FromToInputs
          label="Year"
          from={filters.minYear}
          to={filters.maxYear}
          onFromChange={(v) => setFilter('minYear', v)}
          onToChange={(v) => setFilter('maxYear', v)}
          fromPlaceholder="From"
          toPlaceholder="To"
        />
      </div>

      {/* Advanced filters — collapsed by default, auto-opens when any active. */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <div className="rounded-lg border bg-card">
          <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-sm hover:bg-muted/40 transition-colors">
            <span className="flex items-center gap-2 font-medium">
              Advanced filters
              {activeAdvanced > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                  {activeAdvanced}
                </span>
              )}
            </span>
            <ChevronDown
              className={`size-4 text-muted-foreground transition-transform ${
                advancedOpen ? 'rotate-180' : ''
              }`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FromToInputs
                label="Length (ft)"
                from={filters.minLength !== undefined ? minMToFt(filters.minLength) : undefined}
                to={filters.maxLength !== undefined ? maxMToFt(filters.maxLength) : undefined}
                onFromChange={(v) =>
                  setFilter('minLength', v === undefined ? undefined : ftToMinM(v))
                }
                onToChange={(v) =>
                  setFilter('maxLength', v === undefined ? undefined : ftToMaxM(v))
                }
                fromPlaceholder="From"
                toPlaceholder="To"
              />
              <FromToInputs
                label="Cabins"
                from={filters.minCabins}
                to={filters.maxCabins}
                onFromChange={(v) => setFilter('minCabins', v)}
                onToChange={(v) => setFilter('maxCabins', v)}
                fromPlaceholder="From"
                toPlaceholder="To"
              />
              <FromToInputs
                label="Berths"
                from={filters.minBerths}
                to={filters.maxBerths}
                onFromChange={(v) => setFilter('minBerths', v)}
                onToChange={(v) => setFilter('maxBerths', v)}
                fromPlaceholder="From"
                toPlaceholder="To"
              />
              <Field label="Min WC">
                <NumberInput
                  value={filters.minWc}
                  onChange={(v) => setFilter('minWc', v)}
                  placeholder="Any"
                />
              </Field>
              <Field label="Min People">
                <NumberInput
                  value={filters.minPeople}
                  onChange={(v) => setFilter('minPeople', v)}
                  placeholder="Any"
                />
              </Field>
              {equipmentOptions.length > 0 && (
                <EquipmentFilter
                  equipment={equipmentOptions}
                  selectedIds={filters.equipmentIds}
                  onChange={(ids) => setFilter('equipmentIds', ids)}
                />
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      {children}
    </div>
  );
}

function FromToInputs({
  label,
  from,
  to,
  onFromChange,
  onToChange,
  fromPlaceholder,
  toPlaceholder,
}: {
  label: string;
  from?: number;
  to?: number;
  onFromChange: (v: number | undefined) => void;
  onToChange: (v: number | undefined) => void;
  fromPlaceholder: string;
  toPlaceholder: string;
}) {
  return (
    <Field label={label}>
      <div className="grid grid-cols-2 gap-2">
        <NumberInput value={from} onChange={onFromChange} placeholder={fromPlaceholder} />
        <NumberInput value={to} onChange={onToChange} placeholder={toPlaceholder} />
      </div>
    </Field>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
}: {
  value?: number;
  onChange: (v: number | undefined) => void;
  placeholder: string;
}) {
  return (
    <Input
      type="number"
      inputMode="numeric"
      placeholder={placeholder}
      value={value ?? ''}
      min={0}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '') {
          onChange(undefined);
          return;
        }
        const n = Number(v);
        onChange(Number.isNaN(n) ? undefined : n);
      }}
      className="h-9 text-sm"
    />
  );
}
